import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Avatar,
  Chip,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  useTheme,
  Typography,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Language as LanguageIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  Security as SecurityIcon,
  School as SchoolIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import CustomButton from '../../components/ui/Button/CustomButton';
import { Label } from '../../components/ui/Typography/ThemeTypography';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Gradient header style for cards
const gradientHeaderSx = (theme) => ({
  background: 'linear-gradient(90deg, #ff6b35 0%, #ff9500 100%)',
  color: '#fff',
  borderTopLeftRadius: theme.shape.borderRadius * 2,
  borderTopRightRadius: theme.shape.borderRadius * 2,
  minHeight: 64,
  px: 3,
  py: 2,
  display: 'flex',
  alignItems: 'center',
  boxShadow: '0 2px 8px 0 rgba(255,107,53,0.10)'
});

const sectionCardSx = (theme) => ({
  borderRadius: 3,
  boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)',
  mb: { xs: 3, md: 5 },
  border: `1.5px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
});

const headerCardSx = (theme) => ({
  borderRadius: 3,
  boxShadow: '0 8px 32px 0 rgba(255,107,53,0.10)',
  mb: { xs: 4, md: 6 },
  border: `1.5px solid ${theme.palette.primary.light}`,
  background: 'linear-gradient(90deg, #fff4f0 0%, #fff 100%)',
  position: 'relative',
  overflow: 'visible',
});

const buttonSx = {
  fontWeight: 700,
  borderRadius: 2,
  px: 3,
  py: 1.25,
  fontSize: 16,
  minWidth: 140,
  boxShadow: '0 2px 8px 0 rgba(255,107,53,0.10)'
};

const primaryButtonSx = {
  ...buttonSx,
  background: 'linear-gradient(90deg, #ff6b35 0%, #ff9500 100%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(90deg, #ff9500 0%, #ff6b35 100%)',
    color: '#fff',
  },
};

const secondaryButtonSx = (theme) => ({
  ...buttonSx,
  background: theme.palette.background.paper,
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.primary.main}`,
  '&:hover': {
    background: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
});

/**
 * GeneralSettings component with SafeLine theme matching verification pages
 */
const GeneralSettings = () => {
  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  const { user } = useAuth();
  const [settings, setSettings] = useState({
    schoolName: 'SafeLine Academy',
    timezone: 'America/New_York',
    language: 'en',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    autoLogout: true,
    sessionTimeout: 30,
    enableNotifications: true,
    enableSounds: false,
    enableEmailAlerts: true,
    enableSMSAlerts: false
  });
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load settings on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoadingInitial(true);
        const userId = user?.id;
        
        if (userId) {
          const userSettings = await apiService.settings.getByCategory(userId, 'general');
          if (userSettings && Object.keys(userSettings).length > 0) {
            setSettings(userSettings);
            console.log('Loaded user settings:', userSettings);
          }
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSnackbar({ 
          open: true, 
          message: 'Error loading settings. Using defaults.', 
          severity: 'warning' 
        });
      } finally {
        setLoadingInitial(false);
      }
    };

    loadSettings();
  }, [user]);

  const handleSettingChange = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
  
  const handleSave = async () => {
    if (!user?.id) {
      setSnackbar({ 
        open: true, 
        message: 'Error: User not authenticated', 
        severity: 'error' 
      });
      return;
    }

    setLoading(true);
    try {
      await apiService.settings.save(user.id, 'general', settings);
      setSnackbar({ 
        open: true, 
        message: 'Settings saved successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error saving settings. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleReset = async () => {
    if (!user?.id) {
      setSnackbar({ 
        open: true, 
        message: 'Error: User not authenticated', 
        severity: 'error' 
      });
      return;
    }

    setLoading(true);
    try {
      const defaultSettings = await apiService.settings.reset(user.id, 'general');
      setSettings(defaultSettings.general);
      setSnackbar({ 
        open: true, 
        message: 'Settings reset to defaults', 
        severity: 'info' 
      });
    } catch (error) {
      console.error('Error resetting settings:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error resetting settings. Please try again.', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const settingsSteps = [
    'School Information',
    'Display Preferences', 
    'Security Settings',
    'Notification Preferences'
  ];

  return (
    <Box sx={{}}>
      {loadingInitial ? (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 400,
          flexDirection: 'column',
          gap: 2
        }}>
          <Typography variant="h6" sx={{ color: safeLineColors.mediumCharcoal }}>
            Loading your settings...
          </Typography>
          <Box sx={{ 
            width: 40,
            height: 40,
            border: `3px solid ${safeLineColors.coolGray}`,
            borderTop: `3px solid ${safeLineColors.flameOrange}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }} />
        </Box>
      ) : (
      <Grid container spacing={2}>
        {/* Settings Process Guide */}
        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ color: safeLineColors.flameOrange, mr: 1.5 }} />
                  <Typography variant="h6" sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'bold' }}>
                    Settings Configuration
                  </Typography>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            <CardContent>
              <Stepper orientation="vertical">
                {settingsSteps.map((step, index) => (
                  <Step key={step} active={true}>
                    <StepLabel>
                      <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                        {step}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body2" sx={{ color: safeLineColors.mediumCharcoal }}>
                        {index === 0 && 'Configure school name and timezone settings'}
                        {index === 1 && 'Set language, date and time format preferences'}
                        {index === 2 && 'Manage security and session timeout settings'}
                        {index === 3 && 'Configure notification and alert preferences'}
                      </Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Form */}
        <Grid item xs={12} lg={8}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              backgroundColor: safeLineColors.pureWhite,
              border: `1px solid ${safeLineColors.coolGray}`,
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
              }
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2,
                      backgroundColor: safeLineColors.flameOrange,
                      mr: 2
                    }}>
                      <SchoolIcon sx={{ color: safeLineColors.pureWhite, fontSize: 24 }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        color: safeLineColors.darkCharcoal 
                      }}>
                        General Settings
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: safeLineColors.mediumCharcoal 
                      }}>
                        Configure your school's general settings and preferences
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              }
              sx={{ 
                backgroundColor: safeLineColors.orangeTint,
                borderBottom: `1px solid ${safeLineColors.coolGray}`,
                pb: 2,
                pt: 2
              }}
            />
            
            <CardContent sx={{ p: 3 }}>
              {/* School Information */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <LocationIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  School Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="School Name"
                      value={settings.schoolName}
                      onChange={(e) => handleSettingChange('schoolName', e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Timezone"
                      value={settings.timezone}
                      onChange={(e) => handleSettingChange('timezone', e.target.value)}
                      variant="outlined"
                      select
                      SelectProps={{ native: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    >
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Display Preferences */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <LanguageIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Display Preferences
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Language"
                      value={settings.language}
                      onChange={(e) => handleSettingChange('language', e.target.value)}
                      variant="outlined"
                      select
                      SelectProps={{ native: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Date Format"
                      value={settings.dateFormat}
                      onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                      variant="outlined"
                      select
                      SelectProps={{ native: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Time Format"
                      value={settings.timeFormat}
                      onChange={(e) => handleSettingChange('timeFormat', e.target.value)}
                      variant="outlined"
                      select
                      SelectProps={{ native: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                    >
                      <option value="12h">12-hour</option>
                      <option value="24h">24-hour</option>
                    </TextField>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Security & Session */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <SecurityIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Security & Session
                </Typography>
                <Box sx={{ pl: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.autoLogout}
                        onChange={(e) => handleSettingChange('autoLogout', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: safeLineColors.flameOrange,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: safeLineColors.flameOrange,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                        Auto Logout
                      </Typography>
                    }
                  />
                  <Typography variant="body2" sx={{ 
                    color: safeLineColors.mediumCharcoal, 
                    ml: 5, 
                    mb: 2 
                  }}>
                    Automatically log out users after a period of inactivity
                  </Typography>
                  {settings.autoLogout && (
                    <TextField
                      type="number"
                      label="Session Timeout (minutes)"
                      value={settings.sessionTimeout}
                      onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                      variant="outlined"
                      sx={{ 
                        width: 220, 
                        ml: 5,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: safeLineColors.coolGray,
                          },
                          '&:hover fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: safeLineColors.flameOrange,
                          }
                        }
                      }}
                      inputProps={{ min: 5, max: 120 }}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ borderColor: safeLineColors.coolGray, my: 3 }} />

              {/* Notification Preferences */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ 
                  color: safeLineColors.darkCharcoal, 
                  fontWeight: 'medium',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <NotificationsIcon sx={{ color: safeLineColors.flameOrange, mr: 1, fontSize: 20 }} />
                  Notification Preferences
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableNotifications}
                          onChange={(e) => handleSettingChange('enableNotifications', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                          Push Notifications
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableSounds}
                          onChange={(e) => handleSettingChange('enableSounds', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                          Sound Alerts
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableEmailAlerts}
                          onChange={(e) => handleSettingChange('enableEmailAlerts', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                          Email Notifications
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enableSMSAlerts}
                          onChange={(e) => handleSettingChange('enableSMSAlerts', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: safeLineColors.flameOrange,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: safeLineColors.flameOrange,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: safeLineColors.darkCharcoal, fontWeight: 'medium' }}>
                          SMS Notifications
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading || loadingInitial}
                  sx={{
                    borderColor: safeLineColors.flameOrange,
                    color: safeLineColors.flameOrange,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      backgroundColor: safeLineColors.orangeTint,
                      borderColor: safeLineColors.brightOrange
                    },
                    '&:disabled': {
                      borderColor: safeLineColors.mutedGray,
                      color: safeLineColors.mutedGray
                    }
                  }}
                >
                  {loading ? 'Resetting...' : 'Reset'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading || loadingInitial}
                  sx={{
                    background: loading 
                      ? safeLineColors.mutedGray 
                      : `linear-gradient(135deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`,
                    fontWeight: 'medium',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 3,
                    py: 1.25,
                    '&:hover': {
                      background: loading 
                        ? safeLineColors.mutedGray 
                        : `linear-gradient(135deg, ${safeLineColors.brightOrange}, ${safeLineColors.flameOrange})`
                    },
                    '&:disabled': {
                      background: safeLineColors.mutedGray,
                      color: safeLineColors.pureWhite
                    }
                  }}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GeneralSettings; 